import { roundMoney } from "./formatters";
import { distributeBreakdown, ingresosDeAlquiler } from "./fundBalances";
import { isInWindow, isBeforeWindow, resolveCierreWindow } from "./cierrePeriod";
import { detalleCorto } from "./detalleGasto";

/**
 * Construye la lista de movimientos (extracto bancario) para un fondo específico.
 *
 * Vista de FLUJO DE CAJA: muestra movimientos cuando el dinero realmente se movió.
 * - Débitos: gastos PAGADOS, fecha = paymentDate del registro de pago
 * - Créditos: cobros APROBADOS, fecha = paymentDate del residente
 * - Créditos multas: multas pagadas con feedsReserveFund (solo RESERVA)
 *
 * Fondos:
 * - Caja: gastos_comunes + incidencia → débitos; breakdown.gastos_comunes → créditos
 * - Reserva: gastos "obra" → débitos; breakdown.(fondo_reserva+multas+intereses+obras) → créditos
 */

// --- Helpers ---

function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") {
    // "YYYY-MM-DD" se parsea como medianoche UTC por new Date(), lo que desplaza
    // la fecha un día atrás en zonas horarias negativas (ej: Uruguay UTC-3).
    // Se agrega T12:00:00 para forzar interpretación en hora local.
    const s = val.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(val) ? val + "T12:00:00" : val;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function isInMonth(date, year, month) {
  const d = parseDate(date);
  if (!d) return false;
  return d.getFullYear() === year && d.getMonth() + 1 === month;
}

function isBeforeMonth(date, year, month) {
  const d = parseDate(date);
  if (!d) return false;
  return d.getFullYear() < year || (d.getFullYear() === year && d.getMonth() + 1 < month);
}

/**
 * Extrae el monto que corresponde a un fondo del breakdown de un pago de residente.
 * DELEGA en `distributeBreakdown` (fundBalances.js) — única fuente de verdad del ruteo
 * caja/reserva/custom — para que el extracto y la vista resumen NUNCA diverjan.
 * Antes esta función tenía su propia lógica de ruteo con un `else` que mandaba el
 * amenity/multa entero a reserva aunque estuviera ruteado a caja/custom (doble conteo).
 */
function extractFundAmount(payment, fund) {
  const b = payment.breakdown;
  const paid = Number(payment.paidAmount) || Number(payment.amount) || 0;

  // FIN-032: payments legacy sin breakdown → todo va a Caja (dato histórico, sin migración).
  if (!b) {
    return fund === "caja" ? paid : 0;
  }

  const dist = distributeBreakdown(b, payment, paid);
  // Breakdown vacío / suma 0 → fallback legacy a Caja (mismo criterio que antes).
  if (!dist.hasData) {
    return fund === "caja" ? paid : 0;
  }
  if (fund === "caja") return dist.caja;
  if (fund === "reserva") return dist.reserva;
  return dist.custom[fund] || 0;
}

/** Determina si un gasto pertenece a un fondo dado */
export function expenseBelongsToFund(expense, fund) {
  // Si el gasto tiene sourceFund explícito (obra o incidencia con fondo elegido), manda eso
  // por encima del default legacy por categoría.
  const src = expense.sourceFund;
  if (src) {
    return src === fund;
  }
  // Sin sourceFund: fallback legacy.
  const cat = expense.expenseCategory || "gastos_comunes";
  switch (fund) {
    case "caja":
      return cat !== "obra";
    case "reserva":
      return cat === "obra";
    default:
      // Fondos personalizados solo se asocian vía sourceFund; sin él no reciben gastos.
      return false;
  }
}

/**
 * ¿El gasto se REPARTE entre las unidades como gasto común (sale de la caja)?
 * Criterio único para el cierre de mes y la contabilidad de fondos: si tiene
 * sourceFund manda eso ("reserva"/fondo propio → NO se reparte, lo paga el
 * fondo); sin sourceFund, todo menos "obra". Antes el cierre solo excluía
 * "obra" y repartía las INCIDENCIAS pagadas del fondo de reserva → doble cobro
 * (salían del fondo y además se les cobraban a las unidades).
 */
export function isCommonExpense(expense) {
  return expenseBelongsToFund(expense, "caja");
}

/** Filtra por moneda */
function currencyMatch(item, currency) {
  const m = (item.money || "peso").toLowerCase();
  return currency === "dolar" ? m === "dolar" : m !== "dolar";
}

/** Resuelve la fecha de pago de un cobro de residente */
function resolveResidentPayDate(p) {
  return parseDate(p.paymentDate) || parseDate(p.adminPaymentDate) || parseDate(p.verifiedAt) || parseDate(p.createdAt);
}

/** Resuelve la fecha de cobro de un ingreso del edificio (buildingincomes) */
function resolveIncomeDate(inc) {
  return parseDate(inc.receivedAt) || parseDate(inc.date) || parseDate(inc.createdAt);
}

/** ¿El ingreso corresponde a la moneda de la vista? (USD → dólar; resto → peso) */
function incomeCurrencyMatch(inc, currency) {
  const isUSD = (inc.currency || "UYU").toUpperCase() === "USD";
  return currency === "dolar" ? isUSD : !isUSD;
}

/** Fondo destino de un ingreso (default caja) */
function incomeTargetFund(inc) {
  return inc.targetFundId || "caja";
}

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/**
 * Construye el extracto de movimientos para un fondo y período.
 *
 * @param {Object} params
 * @param {Array} params.expenses - Todos los expenses del edificio
 * @param {Array} params.expensePayments - Payments con expensesid (registros de pago de gastos del admin)
 * @param {Array} params.residentPayments - Payments con userid (cobros de residentes)
 * @param {Array} params.multas - Multas del edificio
 * @param {Array} params.suppliers - Proveedores
 * @param {Array} params.apartments - Unidades
 * @param {Object} params.building - Config del edificio
 * @param {number} params.year
 * @param {number} params.month
 * @param {string} params.fund - "caja" | "reserva"
 * @param {string} [params.currency="peso"] - "peso" | "dolar"
 * @returns {{ saldoInicial, movements, saldoFinal, totalDebitos, totalCreditos }}
 */
export function buildAccountMovements({
  expenses,
  expensePayments,
  residentPayments,
  multas,
  incomes,
  suppliers,
  apartments,
  building,
  year,
  month,
  fund,
  currency = "peso",
  periodStart = null,
  periodEnd = null,
}) {
  const isUSD = currency === "dolar";
  // Ingresos del edificio + alquileres cobrados de unidades del edificio (ver
  // ingresosDeAlquiler): el alquiler es INGRESO del fondo del contrato, no cobranza.
  const ingresos = [...(incomes || []), ...ingresosDeAlquiler(residentPayments)];

  // Ventana de corte configurable: si el cierre define [periodStart, periodEnd],
  // los movimientos se bucketean por esa ventana ((start, end]); si no, por mes
  // calendario (comportamiento histórico). Ver cierrePeriod.js.
  const useWindow = periodStart != null && periodEnd != null;
  const inPeriod = (d) => (useWindow ? isInWindow(d, periodStart, periodEnd) : isInMonth(d, year, month));
  const beforePeriod = (d) => (useWindow ? isBeforeWindow(d, periodStart) : isBeforeMonth(d, year, month));

  // Map de suppliers por id para lookup rápido
  const supplierMap = {};
  (suppliers || []).forEach((s) => {
    if (s.supplierId) supplierMap[String(s.supplierId)] = s;
    supplierMap[s.id] = s;
  });

  // Map de apartments por owner/tenant userid
  const userUnitMap = {};
  (apartments || []).forEach((apt) => {
    const num = apt.number || apt.id;
    if (apt.owner) userUnitMap[String(apt.owner)] = { number: num, tipo: "P" };
    if (apt.tenant) userUnitMap[String(apt.tenant)] = { number: num, tipo: "I" };
  });

  // Map de expense → pago (para obtener fecha de pago)
  // expensePayments son los registros en payments que tienen expensesid
  const expPaymentMap = {};
  (expensePayments || []).forEach((ep) => {
    if (ep.expensesid != null) {
      expPaymentMap[String(ep.expensesid)] = ep;
    }
  });

  // Map de expenses por expensesid
  const expenseMap = {};
  (expenses || []).forEach((e) => {
    if (e.expensesid != null) expenseMap[String(e.expensesid)] = e;
  });

  // --- Initial balance ---
  const initialBalance = getInitialBalance(building, fund, isUSD);

  // --- Calcular saldo anterior (antes del mes seleccionado) ---
  let prevCredits = 0;
  let prevDebits = 0;

  // Débitos previos: gastos pagados con paymentDate antes del mes
  (expensePayments || []).forEach((ep) => {
    const d = parseDate(ep.paymentDate) || parseDate(ep.createdAt);
    if (!d || !beforePeriod(d)) return;
    const expense = expenseMap[String(ep.expensesid)];
    if (!expense) return;
    if (!currencyMatch(expense, currency) || !expenseBelongsToFund(expense, fund)) return;
    prevDebits += Number(ep.amount) || Number(expense.amount) || 0;
  });

  // Créditos previos: cobros de residentes aprobados con paymentDate antes del mes
  (residentPayments || []).forEach((p) => {
    if (p.status !== "aprobado" && !p.paid) return;
    const d = resolveResidentPayDate(p);
    if (!d || !beforePeriod(d)) return;
    prevCredits += extractFundAmount(p, fund);
  });

  // Créditos previos: multas pagadas antes del mes (solo reserva, fuera de cierre)
  if (fund === "reserva") {
    (multas || []).forEach((m) => {
      if (m.status !== "pagada" || !m.feedsReserveFund) return;
      if (m.includedInCierreId) return;
      const d = parseDate(m.paidAt);
      if (!d || !beforePeriod(d)) return;
      prevCredits += Number(m.amount) || 0;
    });
  }

  // Créditos previos: ingresos del edificio cobrados antes del mes, ruteados a este fondo
  ingresos.forEach((inc) => {
    if (inc.status !== "cobrado") return;
    if (incomeTargetFund(inc) !== fund) return;
    if (!incomeCurrencyMatch(inc, currency)) return;
    const d = resolveIncomeDate(inc);
    if (!d || !beforePeriod(d)) return;
    prevCredits += Number(inc.amount) || 0;
  });

  const saldoInicial = roundMoney(initialBalance + prevCredits - prevDebits);

  // --- Movimientos del mes actual ---
  const movements = [];

  // 1. Débitos: gastos pagados este mes (fecha = paymentDate del registro de pago)
  (expensePayments || []).forEach((ep) => {
    const d = parseDate(ep.paymentDate) || parseDate(ep.createdAt);
    if (!d || !inPeriod(d)) return;

    const expense = expenseMap[String(ep.expensesid)];
    if (!expense) return;
    if (!currencyMatch(expense, currency) || !expenseBelongsToFund(expense, fund)) return;

    const amt = Number(ep.amount) || Number(expense.amount) || 0;
    const supplier = supplierMap[String(expense.supplierid)] || supplierMap[String(expense.supplierId)];
    const supplierName = supplier?.name || "";
    const desc = supplierName
      ? `${supplierName}${detalleCorto(expense) ? " - " + detalleCorto(expense) : ""}`
      : detalleCorto(expense) || ep.description || "Gasto";

    movements.push({
      date: d,
      ref: expense.receiptNumber || `G-${expense.expensesid || ""}`,
      type: "GASTO",
      description: desc,
      debit: amt,
      credit: null,
      _sortKey: d.getTime(),
    });
  });

  // 2. Créditos: cobros de residentes aprobados este mes
  (residentPayments || []).forEach((p) => {
    if (p.status !== "aprobado" && !p.paid) return;
    const d = resolveResidentPayDate(p);
    if (!d || !inPeriod(d)) return;

    const fundAmount = extractFundAmount(p, fund);
    if (fundAmount <= 0) return;

    const unit = userUnitMap[String(p.userid)];
    const unitLabel = unit ? `Apt ${unit.number}${unit.tipo}` : `Usuario ${String(p.userid).slice(-4)}`;
    const monthName = MONTHS[(p.month || month) - 1] || "";

    movements.push({
      date: d,
      ref: `COB-${unit ? unit.number + unit.tipo : String(p.userid).slice(-4)}`,
      type: "COBRANZA",
      description: `${unitLabel} - ${monthName} ${p.year || year}`,
      debit: null,
      credit: roundMoney(fundAmount),
      _sortKey: d.getTime(),
    });
  });

  // 3. Créditos multas (solo RESERVA, fuera de cierre)
  if (fund === "reserva") {
    (multas || []).forEach((m) => {
      if (m.status !== "pagada" || !m.feedsReserveFund) return;
      if (m.includedInCierreId) return;
      const d = parseDate(m.paidAt);
      if (!d || !inPeriod(d)) return;

      const unit = userUnitMap[String(m.userid)];
      const unitLabel = unit ? `Apt ${unit.number}${unit.tipo}` : "";

      movements.push({
        date: d,
        ref: `MUL-${m.id?.slice(-4) || ""}`,
        type: "MULTA COBRADA",
        description: `${unitLabel}${m.reason ? " - " + m.reason : ""}`.trim(),
        debit: null,
        credit: Number(m.amount) || 0,
        _sortKey: d.getTime(),
      });
    });
  }

  // 4. Créditos: ingresos del edificio cobrados este mes, ruteados a este fondo
  ingresos.forEach((inc) => {
    if (inc.status !== "cobrado") return;
    if (incomeTargetFund(inc) !== fund) return;
    if (!incomeCurrencyMatch(inc, currency)) return;
    const d = resolveIncomeDate(inc);
    if (!d || !inPeriod(d)) return;
    movements.push({
      date: d,
      ref: inc.ref || `ING-${String(inc.incomeid || inc.id || "").slice(-4)}`,
      type: "INGRESO",
      description: `${inc.concept || "Ingreso"}${inc.counterparty ? " - " + inc.counterparty : ""}`.trim(),
      debit: null,
      credit: roundMoney(Number(inc.amount) || 0),
      _sortKey: d.getTime(),
    });
  });

  // Ordenar por fecha ASC
  movements.sort((a, b) => {
    if (a._sortKey !== b._sortKey) return a._sortKey - b._sortKey;
    if (a.credit && !b.credit) return -1;
    if (!a.credit && b.credit) return 1;
    return 0;
  });

  // Calcular saldo corrido
  let balance = saldoInicial;
  let totalDebitos = 0;
  let totalCreditos = 0;

  movements.forEach((m) => {
    if (m.credit) {
      balance += m.credit;
      totalCreditos += m.credit;
    }
    if (m.debit) {
      balance -= m.debit;
      totalDebitos += m.debit;
    }
    m.balance = roundMoney(balance);
    delete m._sortKey;
  });

  const saldoFinal = roundMoney(balance);

  return {
    saldoInicial,
    movements,
    saldoFinal,
    totalDebitos: roundMoney(totalDebitos),
    totalCreditos: roundMoney(totalCreditos),
  };
}

/**
 * Serie de saldos de fin de mes de un fondo, para graficar la evolución mensual.
 * Reutiliza `buildAccountMovements` mes a mes (misma lógica de flujo de caja),
 * así el saldo del último mes coincide con `computeCurrentFundBalances`.
 *
 * @param {Object} params  - mismos datos que buildAccountMovements + { months, now }
 * @param {number} [params.months=6]
 * @param {Date}   [params.now]
 * @returns {Array<{ label:string, month:number, year:number, saldo:number,
 *   creditos:number, debitos:number }>} más antiguo → más reciente
 */
export function buildFundBalanceSeries({ months = 6, now = new Date(), diaCorte = null, cierresByKey = null, ...rest }) {
  const series = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    // Si el edificio tiene día de corte (o hay un cierre con ventana explícita), cada
    // punto de la serie se calcula por su ventana; si no, por mes calendario.
    let periodStart = null;
    let periodEnd = null;
    if (diaCorte != null || cierresByKey) {
      const cierreDoc = cierresByKey ? cierresByKey[`${year}_${month}`] : null;
      const w = resolveCierreWindow(month, year, { diaCorte, cierreDoc });
      periodStart = w.start;
      periodEnd = w.end;
    }
    const { saldoFinal, totalCreditos, totalDebitos } = buildAccountMovements({ ...rest, year, month, periodStart, periodEnd });
    series.push({
      label: MONTHS[d.getMonth()].slice(0, 3),
      month,
      year,
      saldo: saldoFinal,
      creditos: totalCreditos,
      debitos: totalDebitos,
    });
  }
  return series;
}

function getInitialBalance(building, fund, isUSD) {
  if (!building) return 0;
  switch (fund) {
    case "caja":
      return Number(isUSD ? building.initialBankBalanceUSD : building.initialBankBalance) || 0;
    case "reserva":
      return Number(isUSD ? building.initialReserveFundUSD : building.initialReserveFund) || 0;
    default: {
      // Fondos personalizados: buscar en customFunds del edificio
      const cf = (building?.customFunds || []).find(f => f.id === fund);
      return Number(isUSD ? cf?.initialBalanceUSD : cf?.initialBalance) || 0;
    }
  }
}

/**
 * Calcula el saldo actual de cada fondo sumando todos los movimientos históricos.
 * Usa la misma lógica de flujo de caja que buildAccountMovements.
 */
export function computeCurrentFundBalances({
  expenses,
  expensePayments,
  residentPayments,
  multas,
  incomes,
  building,
  currency = "peso",
}) {
  const isUSD = currency === "dolar";
  const ingresos = [...(incomes || []), ...ingresosDeAlquiler(residentPayments)];

  const expenseMap = {};
  (expenses || []).forEach((e) => {
    if (e.expensesid != null) expenseMap[String(e.expensesid)] = e;
  });

  let cajaDb = 0, cajaCr = 0;
  let reservaDb = 0, reservaCr = 0;

  // Débitos: gastos pagados (todos los meses)
  (expensePayments || []).forEach((ep) => {
    const d = parseDate(ep.paymentDate) || parseDate(ep.createdAt);
    if (!d) return;
    const expense = expenseMap[String(ep.expensesid)];
    if (!expense || !currencyMatch(expense, currency)) return;
    const amt = Number(ep.amount) || Number(expense.amount) || 0;
    if (expenseBelongsToFund(expense, "caja")) cajaDb += amt;
    if (expenseBelongsToFund(expense, "reserva")) reservaDb += amt;
  });

  // Créditos: cobros de residentes aprobados (todos los meses)
  (residentPayments || []).forEach((p) => {
    if (p.status !== "aprobado" && !p.paid) return;
    const d = resolveResidentPayDate(p);
    if (!d) return;
    cajaCr += extractFundAmount(p, "caja");
    reservaCr += extractFundAmount(p, "reserva");
  });

  // Créditos multas: solo reserva, fuera de cierre
  (multas || []).forEach((m) => {
    if (m.status !== "pagada" || !m.feedsReserveFund) return;
    if (m.includedInCierreId) return;
    const d = parseDate(m.paidAt);
    if (!d) return;
    reservaCr += Number(m.amount) || 0;
  });

  // Créditos ingresos del edificio cobrados, ruteados a caja/reserva
  ingresos.forEach((inc) => {
    if (inc.status !== "cobrado") return;
    if (!incomeCurrencyMatch(inc, currency)) return;
    const d = resolveIncomeDate(inc);
    if (!d) return;
    const tf = incomeTargetFund(inc);
    const amt = Number(inc.amount) || 0;
    if (tf === "caja") cajaCr += amt;
    else if (tf === "reserva") reservaCr += amt;
  });

  const initCaja = getInitialBalance(building, "caja", isUSD);
  const initReserva = getInitialBalance(building, "reserva", isUSD);

  // Fondos personalizados (FIN-019)
  const customBalances = {};
  (building?.customFunds || []).filter(f => f.active !== false).forEach(fund => {
    let cr = 0;
    let db = 0;
    // Créditos desde pagos de residentes
    (residentPayments || []).forEach(p => {
      if (p.status !== "aprobado" && !p.paid) return;
      const d = resolveResidentPayDate(p);
      if (!d) return;
      cr += extractFundAmount(p, fund.id);
    });
    // Créditos desde ingresos del edificio ruteados a este fondo custom
    ingresos.forEach((inc) => {
      if (inc.status !== "cobrado") return;
      if (!incomeCurrencyMatch(inc, currency)) return;
      if (incomeTargetFund(inc) !== fund.id) return;
      const d = resolveIncomeDate(inc);
      if (!d) return;
      cr += Number(inc.amount) || 0;
    });
    // Débitos: gastos pagados con sourceFund = fund.id (FIN-019 – obras o incidencias
    // que el admin asignó explícitamente a este fondo personalizado).
    (expensePayments || []).forEach((ep) => {
      const d = parseDate(ep.paymentDate) || parseDate(ep.createdAt);
      if (!d) return;
      const expense = expenseMap[String(ep.expensesid)];
      if (!expense || !currencyMatch(expense, currency)) return;
      if (!expenseBelongsToFund(expense, fund.id)) return;
      db += Number(ep.amount) || Number(expense.amount) || 0;
    });
    const init = getInitialBalance(building, fund.id, isUSD);
    customBalances[fund.id] = roundMoney(init + cr - db);
  });

  return {
    caja: roundMoney(initCaja + cajaCr - cajaDb),
    reserva: roundMoney(initReserva + reservaCr - reservaDb),
    ...customBalances,
  };
}

/**
 * Saldo total en pesos: la suma de las cuentas del edificio (caja + reserva +
 * fondos propios activos), con los saldos de computeCurrentFundBalances.
 */
export function saldoTotalDeFondos(balances, customFunds = []) {
  const activos = (customFunds || []).filter((f) => f && f.active !== false);
  const total = ["caja", "reserva", ...activos.map((f) => f.id)]
    .reduce((s, k) => s + (Number(balances?.[k]) || 0), 0);
  return roundMoney(total);
}
